/**
 * Ordenação e destaque dos resultados do menu de barra.
 *
 * O PROBLEMA QUE ISTO RESOLVE
 *
 * O backend busca com `ILIKE %termo%` — pedaço em qualquer lugar. Digitar "an"
 * trazia "jane", e o resultado parece quebrado: a pessoa não vê o "an" no
 * começo de nada e conclui que o filtro não funciona. Foi a reclamação do
 * Victor em 2026-08-06.
 *
 * Medido no Slack no mesmo dia: `/pand` não devolve `/expandir`, embora
 * "expandir" contenha "pand". O Slack casa **começo de palavra** — e no Cmd+K
 * a letra casada vem em **negrito**, que é o que explica por que aquela linha
 * apareceu.
 *
 * Duas peças, então: `ranquear` (quem vem primeiro) e `destacar` (por quê).
 *
 * POR QUE RANQUEAR EM VEZ DE FILTRAR
 *
 * Descartar quem só casa no meio esconderia resultado legítimo — buscar "silva"
 * e não achar "João da Silva-Souza" seria pior que a doença. Então quem casa no
 * começo sobe, quem casa no meio desce, ninguém some.
 */

// Sem acento e sem caixa: "João" tem que casar com "joao", e "ÂNGELA" com "an".
// Advogado digita rápido e sem acento.
const semAcento = t =>
  t
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const normalizar = texto => semAcento(String(texto ?? ''));

/**
 * Normaliza mantendo, para cada caractere do resultado, de qual posição do
 * ORIGINAL ele veio.
 *
 * Existe porque tirar acento nem sempre preserva o comprimento: "ã" já composto
 * ocupa 1 caractere e vira 1, mas "ã" em forma decomposta (a + til combinante,
 * como às vezes vem de copiar e colar) ocupa 2 e vira 1. Cortar o texto
 * original por um índice achado no texto normalizado erraria a fatia nesse
 * caso, e o negrito sairia deslocado no meio do nome do cliente.
 *
 * Caractere a caractere é O(n) e roda sobre no máximo seis linhas de menu.
 *
 * @returns {{ norm: string, mapa: number[] }}
 */
const normalizarComMapa = texto => {
  const original = String(texto ?? '');
  let norm = '';
  const mapa = [];

  for (let i = 0; i < original.length; i += 1) {
    const pedaco = semAcento(original[i]);
    for (let j = 0; j < pedaco.length; j += 1) mapa.push(i);
    norm += pedaco;
  }
  // Fecha o mapa: uma casada que termina no fim do texto precisa deste índice.
  mapa.push(original.length);

  return { norm, mapa };
};

// Palavra começa depois de qualquer coisa que não seja letra ou dígito. Pega
// espaço, mas também ponto e hífen — que é como CNJ e CPF são escritos
// ("0000859-77.2024" tem quatro "palavras", e buscar "77" acha a segunda).
const SEPARADOR = /[^\p{L}\p{N}]+/u;

export const PONTUACAO = {
  PASTA: -1, // é EXATAMENTE a pasta pedida — ver `pontuarPasta`
  COMECO: 0, // o texto inteiro começa com o termo
  PALAVRA: 1, // alguma palavra começa com o termo
  MEIO: 2, // aparece, mas no meio de uma palavra
  NENHUM: 3, // não aparece
};

/**
 * A pasta pedida vem primeiro, sempre.
 *
 * Victor, 09/09/2026: "digitei /processo 261 e nem apareceu a opção do
 * Proc - 0000261". O processo estava entre os resultados — só afundava.
 * "261" casa no MEIO de "0000261", que pontua igual a qualquer CNJ com 261 no
 * meio, e o menu mostra seis linhas.
 *
 * Comparar por número, e não por texto, é o que faz "261", "0261" e
 * "Proc - 0000261" serem a mesma pergunta.
 *
 * @returns {number|null} `PONTUACAO.PASTA` quando é a pasta exata; `null`
 *   quando a regra não se aplica e a pontuação normal decide.
 */
export const pontuarPasta = (pasta, termo) => {
  const digitosDoTermo = String(termo ?? '').replace(/\D/g, '');
  const digitosDaPasta = String(pasta ?? '').replace(/\D/g, '');
  if (!digitosDoTermo || !digitosDaPasta) return null;
  // Só quando o termo é SÓ número: "millena 261" é busca de texto, não de
  // pasta.
  if (/[a-zà-ú]/i.test(String(termo).replace(/proc/i, ''))) return null;
  if (Number(digitosDoTermo) !== Number(digitosDaPasta)) return null;

  return PONTUACAO.PASTA;
};

/**
 * Quão bem `texto` casa com `termo`. Menor é melhor.
 */
export const pontuar = (texto, termo) => {
  const t = normalizar(texto);
  const q = normalizar(termo).trim();
  if (!q) return PONTUACAO.NENHUM;
  if (t.startsWith(q)) return PONTUACAO.COMECO;
  if (t.split(SEPARADOR).some(p => p.startsWith(q))) return PONTUACAO.PALAVRA;
  if (t.includes(q)) return PONTUACAO.MEIO;
  return PONTUACAO.NENHUM;
};

/**
 * Ordena os resultados. O subtítulo conta porque é onde moram CNJ e CPF — quem
 * cola um número de processo casa ali, não no título.
 *
 * `sort` do JS é estável desde o ES2019, então empate preserva a ordem que o
 * backend devolveu (que já é a ordem que aquela tela considera relevante).
 */
export const ranquear = (itens, termo) =>
  itens
    .map(item => ({
      item,
      pontos:
        pontuarPasta(item.pasta, termo) ??
        Math.min(pontuar(item.titulo, termo), pontuar(item.subtitulo, termo)),
    }))
    .sort((a, b) => a.pontos - b.pontos)
    .map(({ item }) => item);

/**
 * Quebra o texto em pedaços marcando o que casou, para o template pôr em
 * negrito.
 *
 * Devolve pedaços em vez de HTML de propósito: montar `<strong>` aqui exigiria
 * `v-html`, e aí o nome de um cliente com `<` viraria injeção. O template
 * interpola cada pedaço como texto.
 *
 * @returns {Array<{ texto: string, forte: boolean }>}
 */
export const destacar = (texto, termo) => {
  const original = String(texto ?? '');
  const q = normalizar(termo).trim();
  if (!q || !original) return [{ texto: original, forte: false }];

  // Casa sobre a versão sem acento, mas fatia a ORIGINAL — só assim "João"
  // aparece com o acento depois de ter casado por "joao". O mapa traduz a
  // posição de um texto para o outro.
  const { norm, mapa } = normalizarComMapa(original);
  const em = norm.indexOf(q);
  if (em === -1) return [{ texto: original, forte: false }];

  const inicio = mapa[em];
  const fim = mapa[em + q.length];

  return [
    { texto: original.slice(0, inicio), forte: false },
    { texto: original.slice(inicio, fim), forte: true },
    { texto: original.slice(fim), forte: false },
  ].filter(p => p.texto !== '');
};
